import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import ErrorBoundary from './ErrorBoundary';
import ErrorFallback from './ErrorFallback';
import ErrorTracker from '../services/errorTracker';
import Logger from '../services/logger';

interface ErrorBoundaryContextType {
  reportError: (error: Error, errorInfo: any, componentName?: string) => void;
  clearErrors: () => void;
  getErrorCount: () => number;
}

const ErrorBoundaryContext = createContext<ErrorBoundaryContextType | undefined>(undefined);

interface ErrorBoundaryProviderProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: any) => void;
  maxRetries?: number;
  resetOnPropsChange?: boolean;
}

interface ErrorState {
  error: Error | null;
  errorInfo: any;
  componentName: string;
  retryCount: number;
  timestamp: number;
}

export const ErrorBoundaryProvider: React.FC<ErrorBoundaryProviderProps> = ({
  children,
  fallback,
  onError,
  maxRetries = 3,
  resetOnPropsChange = true
}) => {
  const [errors, setErrors] = useState<ErrorState[]>([]);
  const [globalError, setGlobalError] = useState<ErrorState | null>(null);

  const reportError = useCallback((error: Error, errorInfo: any, componentName: string = 'Unknown') => {
    const errorState: ErrorState = {
      error,
      errorInfo,
      componentName,
      retryCount: 0,
      timestamp: Date.now()
    };

    // Track error
    ErrorTracker.trackError(error, `ErrorBoundaryProvider: ${componentName}`, {
      component: componentName,
      errorInfo: errorInfo?.componentStack,
      timestamp: errorState.timestamp
    });

    // Log error
    Logger.error(`Error reported in ${componentName}`, error, {
      componentStack: errorInfo?.componentStack,
      timestamp: errorState.timestamp
    });

    // Add to errors list
    setErrors(prev => [...prev, errorState]);

    // Set as global error if none exists
    if (!globalError) {
      setGlobalError(errorState);
    }

    // Call custom error handler
    onError?.(error, errorInfo);
  }, [globalError, onError]);

  const clearErrors = useCallback(() => {
    setErrors([]);
    setGlobalError(null);
  }, []);

  const getErrorCount = useCallback(() => {
    return errors.length;
  }, [errors.length]);

  const handleError = useCallback((error: Error, errorInfo: any) => {
    reportError(error, errorInfo, 'ErrorBoundary');
  }, [reportError]);

  const contextValue: ErrorBoundaryContextType = {
    reportError,
    clearErrors,
    getErrorCount
  };

  return (
    <ErrorBoundaryContext.Provider value={contextValue}>
      <ErrorBoundary
        onError={handleError}
        fallback={fallback}
        resetOnPropsChange={resetOnPropsChange}
        resetKeys={globalError?.timestamp ? [globalError.timestamp] : []}
      >
        {children}
      </ErrorBoundary>
    </ErrorBoundaryContext.Provider>
  );
};

export const useErrorBoundary = (): ErrorBoundaryContextType => {
  const context = useContext(ErrorBoundaryContext);
  if (context === undefined) {
    throw new Error('useErrorBoundary must be used within an ErrorBoundaryProvider');
  }
  return context;
};

// Higher-order component for wrapping individual components
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  componentName?: string,
  fallback?: ReactNode
) => {
  const WrappedComponent = (props: P) => {
    const { reportError } = useErrorBoundary();

    const handleError = useCallback((error: Error, errorInfo: any) => {
      reportError(error, errorInfo, componentName || Component.name);
    }, [reportError, componentName]);

    return (
      <ErrorBoundary
        onError={handleError}
        fallback={fallback || <ErrorFallback 
          error={new Error('Component Error')} 
          resetError={() => window.location.reload()} 
          retryCount={0}
          componentName={componentName || Component.name}
        />}
        resetOnPropsChange={true}
      >
        <Component {...props} />
      </ErrorBoundary>
    );
  };

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

// Hook for manual error reporting
export const useErrorHandler = () => {
  const { reportError } = useErrorBoundary();

  const handleError = useCallback((error: Error, context?: any) => {
    reportError(error, context, 'Manual Error Report');
  }, [reportError]);

  return handleError;
};

export default ErrorBoundaryProvider;
